import React, { useState, useEffect } from 'react';
import { Layout, Card, Table, Button, Modal, Form, Input, Select, message, Space, Typography, DatePicker, Row, Col, Statistic, Tag, Popconfirm } from 'antd';
import { PlusOutlined, ReloadOutlined, ArrowDownOutlined, DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../utils/api';
import { formatCurrency } from '../../utils/helpers';

const { Title, Text } = Typography;
const { Option } = Select;

const BankDepositsPage = () => {
    const [deposits, setDeposits] = useState([]);
    const [bankAccounts, setBankAccounts] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchDeposits();
        fetchBankAccounts();
        fetchGlAccounts();
    }, []);

    const fetchDeposits = async () => {
        setLoading(true);
        try {
            const res = await api.get('/api/accounting/bank-deposits');
            if (res.data.success) {
                setDeposits(res.data.data);
            }
        } catch (error) {
            message.error('ไม่สามารถดึงข้อมูลรายการฝากเงินได้');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/api/accounting/bank-deposits/${id}`);
            message.success('ลบรายการฝากเงินและปรับปรุงยอดเงินเรียบร้อย');
            fetchDeposits();
        } catch (error) {
            message.error('ไม่สามารถลบรายการได้');
        }
    };

    const fetchBankAccounts = async () => {
        try {
            const res = await api.get('/api/accounting/bank-accounts');
            if (res.data.success) {
                setBankAccounts(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch bank accounts');
        }
    };

    const fetchGlAccounts = async () => {
        try {
            const res = await api.get('/api/accounting/accounts');
            if (res.data.success) {
                setGlAccounts(res.data.data);
            }
        } catch (error) {
            console.error('Failed to fetch GL accounts');
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const res = await api.post('/api/accounting/bank-deposits', {
                ...values,
                deposit_date: values.deposit_date.format('YYYY-MM-DD')
            });
            if (res.data.success) {
                message.success(`บันทึกรายการฝากเงินเรียบร้อย (JV: ${res.data.data.jv_number})`);
                setIsModalVisible(false);
                form.resetFields();
                fetchDeposits();
            }
        } catch (error) {
            message.error(error.response?.data?.message || 'ไม่สามารถบันทึกรายการฝากเงินได้');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'วันที่',
            dataIndex: 'deposit_date',
            key: 'deposit_date',
            render: (date) => dayjs(date).format('DD/MM/YYYY')
        },
        {
            title: 'เข้าบัญชีธนาคาร',
            dataIndex: 'bank_account_name',
            key: 'bank_account_name',
            render: (text, record) => (
                <span>{text} ({record.bank_name || '-'})</span>
            )
        },
        {
            title: 'จากบัญชี (GL)',
            dataIndex: 'source_account_name',
            key: 'source_account_name'
        },
        {
            title: 'จำนวนเงิน',
            dataIndex: 'amount',
            key: 'amount',
            align: 'right',
            render: (amount) => <Text strong style={{ color: '#52c41a' }}>{formatCurrency(amount)}</Text>
        },
        {
            title: 'เลขที่ JV',
            dataIndex: 'jv_number',
            key: 'jv_number',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: 'อ้างอิง',
            dataIndex: 'reference_no',
            key: 'reference_no'
        },
        {
            title: 'หมายเหตุ',
            dataIndex: 'notes',
            key: 'notes'
        },
        {
            title: 'จัดการ',
            key: 'actions',
            render: (_, record) => (
                <Popconfirm
                    title="ยืนยันการลบรายการ?"
                    description="ยอดเงินในบัญชีธนาคารจะถูกหักออก และรายการรายวันจะถูกลบ"
                    onConfirm={() => handleDelete(record.id)}
                    okText="ใช่"
                    cancelText="ไม่"
                >
                    <Button icon={<DeleteOutlined />} size="small" danger />
                </Popconfirm>
            )
        }
    ];

    return (
        <Layout style={{ padding: '24px' }}>
            <Card>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                    <Title level={2}>ฝากเงินเข้าบัญชี (Bank Deposits)</Title>
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={fetchDeposits}>รีเฟรช</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => {
                            form.resetFields();
                            form.setFieldsValue({ deposit_date: dayjs() });
                            setIsModalVisible(true);
                        }}>บันทึกการฝากเงิน</Button>
                    </Space>
                </div>

                <Row gutter={16} style={{ marginBottom: '24px' }}>
                    <Col span={8}>
                        <Card size="small">
                            <Statistic
                                title="ยอดเงินฝากรวม (เดือนนี้)"
                                value={deposits.filter(d => dayjs(d.deposit_date).isSame(dayjs(), 'month')).reduce((sum, d) => sum + parseFloat(d.amount), 0)}
                                precision={2}
                                prefix={<ArrowDownOutlined />}
                                valueStyle={{ color: '#3f8600' }}
                                suffix="บาท"
                            />
                        </Card>
                    </Col>
                </Row>

                <Table
                    columns={columns}
                    dataSource={deposits}
                    rowKey="id"
                    loading={loading}
                />
            </Card>

            <Modal
                title="บันทึกการฝากเงินเข้าบัญชี"
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                confirmLoading={loading}
                okText="บันทึกรายการ"
                cancelText="ยกเลิก"
            >
                <Form form={form} layout="vertical" onFinish={handleSubmit}>
                    <Form.Item name="deposit_date" label="วันที่ฝากเงิน" rules={[{ required: true }]}>
                        <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                    </Form.Item>

                    <Form.Item name="bank_account_id" label="เข้าบัญชีธนาคาร" rules={[{ required: true }]}>
                        <Select placeholder="เลือกบัญชีธนาคารปลายทาง">
                            {bankAccounts.map(acc => (
                                <Option key={acc.id} value={acc.id}>
                                    {acc.account_name} ({acc.bank_name}) - {acc.account_number}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="source_account_id" label="จากบัญชี (GL)" rules={[{ required: true }]} tooltip="เลือกบัญชีต้นทาง เช่น เงินสด หรือ รายได้">
                        <Select
                            showSearch
                            placeholder="ค้นหาบัญชี (เช่น เงินสด, รายได้)"
                            optionFilterProp="children"
                        >
                            {glAccounts.map(acc => (
                                <Option key={acc.id} value={acc.id}>
                                    {acc.CODE || acc.code} - {acc.NAME || acc.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item name="amount" label="จำนวนเงิน" rules={[{ required: true }]}>
                        <Input type="number" step="0.01" prefix="฿" placeholder="0.00" />
                    </Form.Item>

                    <Form.Item name="reference_no" label="เลขที่อ้างอิง">
                        <Input placeholder="เช่น เลขที่ใบนำฝาก" />
                    </Form.Item>

                    <Form.Item name="notes" label="หมายเหตุ">
                        <Input.TextArea rows={2} placeholder="รายละเอียดเพิ่มเติม" />
                    </Form.Item>
                </Form>
            </Modal>
        </Layout>
    );
};

export default BankDepositsPage;
